/**
 * PURPOSE: Filtros compartidos por las cuatro vistas del Centro de Programación (FTR-0028 WG3, §8).
 * RESPONSIBILITIES: Un único juego de filtros — grupo, jornada, sede, cancha, fase, estado y
 *   búsqueda — que afecta a las cuatro vistas por igual.
 *
 *   COMPARTIDOS DE VERDAD. Viven en el proveedor, no aquí: cambiar de vista no los reinicia y no hay
 *   forma de que dos vistas muestren conjuntos distintos de la misma consulta.
 *
 *   LAS HUÉRFANAS SE MUESTRAN A PROPÓSITO. Son estructura retirada que conserva su programación por
 *   si su configuración vuelve; se ocultan por defecto para no ensuciar la operación, pero con un
 *   interruptor visible, nunca en silencio.
 * COLLABORATORS: ProgrammingCenterProvider.jsx, utils/programmingCenterGrouping.js.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/components/FiltersBar.jsx
 */
import React from "react";
import { FiFilter, FiSearch, FiX } from "react-icons/fi";

import { useProgrammingCenter } from "../ProgrammingCenterProvider.jsx";
import { STATUS_FILTERS } from "../../../../utils/programmingCenterGrouping.js";

const selectClass =
  "rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm font-bold text-slate-700 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100";

const FiltersBar = () => {
  const { filters, setFilter, resetFilters, hasFilters, filterOptions, visibleRows, rows } =
    useProgrammingCenter();

  const courts =
    filterOptions.venues.find((venue) => Number(venue.value) === Number(filters.idsede))?.courts ||
    [];

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-4 py-2">
      <FiFilter className="text-sm text-slate-400" aria-hidden="true" />

      <label className="sr-only" htmlFor="pc-filter-group">
        Grupo
      </label>
      <select
        id="pc-filter-group"
        value={filters.groupNo}
        onChange={(event) => setFilter("groupNo", event.target.value)}
        className={selectClass}
      >
        <option value="">Todos los grupos</option>
        {filterOptions.groups.map((group) => (
          <option key={group.value} value={group.value}>
            {group.label}
          </option>
        ))}
      </select>

      <label className="sr-only" htmlFor="pc-filter-date">
        Jornada
      </label>
      <select
        id="pc-filter-date"
        value={filters.dateNo}
        onChange={(event) => setFilter("dateNo", event.target.value)}
        className={selectClass}
      >
        <option value="">Todas las jornadas</option>
        {filterOptions.dates.map((date) => (
          <option key={`${date.phase}-${date.value}`} value={date.value}>
            {date.label}
          </option>
        ))}
      </select>

      <label className="sr-only" htmlFor="pc-filter-phase">
        Fase
      </label>
      <select
        id="pc-filter-phase"
        value={filters.phase}
        onChange={(event) => setFilter("phase", event.target.value)}
        className={selectClass}
      >
        <option value="">Todas las fases</option>
        <option value="1">Fase de grupos</option>
        <option value="2">Fase final</option>
      </select>

      <label className="sr-only" htmlFor="pc-filter-venue">
        Sede
      </label>
      <select
        id="pc-filter-venue"
        value={filters.idsede}
        onChange={(event) => {
          setFilter("idsede", event.target.value);
          setFilter("idcancha", "");
        }}
        className={selectClass}
      >
        <option value="">Todas las sedes</option>
        {filterOptions.venues.map((venue) => (
          <option key={venue.value} value={venue.value}>
            {venue.label}
          </option>
        ))}
      </select>

      <label className="sr-only" htmlFor="pc-filter-court">
        Cancha
      </label>
      <select
        id="pc-filter-court"
        value={filters.idcancha}
        onChange={(event) => setFilter("idcancha", event.target.value)}
        disabled={!filters.idsede}
        className={`${selectClass} disabled:bg-slate-50 disabled:text-slate-400`}
      >
        <option value="">Todas las canchas</option>
        {courts.map((court) => (
          <option key={court.value} value={court.value}>
            {court.label}
            {court.retired ? " (retirada)" : ""}
          </option>
        ))}
      </select>

      <label className="sr-only" htmlFor="pc-filter-status">
        Estado
      </label>
      <select
        id="pc-filter-status"
        value={filters.status}
        onChange={(event) => setFilter("status", event.target.value)}
        className={selectClass}
      >
        {STATUS_FILTERS.map((status) => (
          <option key={status.value} value={status.value}>
            {status.label}
          </option>
        ))}
      </select>

      <div className="relative">
        <FiSearch className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-slate-400" />
        <input
          type="search"
          value={filters.search}
          onChange={(event) => setFilter("search", event.target.value)}
          placeholder="Buscar slot, institución, sede…"
          aria-label="Buscar partidos"
          className={`${selectClass} w-52 pl-7`}
        />
      </div>

      <label className="flex cursor-pointer items-center gap-1.5 text-xs font-black text-slate-600">
        <input
          type="checkbox"
          checked={filters.showOrphans}
          onChange={(event) => setFilter("showOrphans", event.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400"
        />
        Ver huérfanos
      </label>

      {hasFilters ? (
        <button
          type="button"
          onClick={resetFilters}
          className="flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-black text-slate-500 transition hover:border-rose-300 hover:text-rose-600"
        >
          <FiX className="text-xs" />
          Limpiar
        </button>
      ) : null}

      <span className="ml-auto text-xs font-black text-slate-500">
        {visibleRows.length} de {rows.length}
      </span>
    </div>
  );
};

export default FiltersBar;
